import { getLocales } from "expo-localization";

export const supportedSiteLocales = [
  "zh",
  "en",
  "es",
  "fr",
  "ja",
  "ko",
  "ar",
  "hi",
  "pt",
  "ru",
  "th",
] as const;

export type SiteLocale = (typeof supportedSiteLocales)[number];

function localeFromLanguageTag(value: string): SiteLocale {
  const normalized = value.toLowerCase();
  return (
    supportedSiteLocales.find(
      (candidate) =>
        normalized === candidate || normalized.startsWith(`${candidate}-`),
    ) || "en"
  );
}

function localeFromWebPath(): SiteLocale | undefined {
  if (typeof window === "undefined") return undefined;
  const parts = window.location.pathname.split("/").filter(Boolean);
  if (parts[0] === "tiktok") parts.shift();
  return supportedSiteLocales.includes(parts[0] as SiteLocale)
    ? (parts[0] as SiteLocale)
    : undefined;
}

const deviceLanguage = getLocales()[0]?.languageTag ?? "en";
export const siteLocale = localeFromWebPath() || localeFromLanguageTag(deviceLanguage);
export const appLanguage = siteLocale === "zh" ? "zh" : "en";

const siteTitles: Record<SiteLocale, string> = {
  zh: "海皇娱乐 · Neptune Entertainment",
  en: "Neptune Entertainment",
  es: "Neptune Entertainment",
  fr: "Neptune Entertainment",
  ja: "ネプチューン・エンターテインメント",
  ko: "넵튠 엔터테인먼트",
  ar: "نبتون إنترتينمنت",
  hi: "नेपच्यून एंटरटेनमेंट",
  pt: "Neptune Entertainment",
  ru: "Neptune Entertainment",
  th: "เนปจูน เอนเตอร์เทนเมนต์",
};

export function localizedSiteTitle() {
  return siteTitles[siteLocale];
}

const translations = {
  en: {
    tabFeed: "Home",
    tabColumns: "Columns",
    tabDiscover: "Discover",
    tabAdd: "Add",
    tabInbox: "Inbox",
    tabMe: "Me",
    columnsTitle: "Columns",
    all: "All",
    freshVideos: "Fresh videos",
    games: "Games",
    music: "Music",
    shorts: "Shorts",
    live: "Live",
    searchPlaceholder: "Search videos",
    castSearching: "Searching for cast devices...",
    notificationsEnabled: "Notifications enabled",
    notificationsDisabled: "Notifications disabled",
    noVideos: "No videos found",
    profileFollowing: "Following",
    profileFollowers: "Followers",
    profileLikes: "Likes",
    profileEdit: "Edit",
    profileEditProfile: "Edit Profile",
    profileFollow: "Follow",
    profileMessage: "Message",
    profileDisplayName: "Display Name",
    demoUserName: "Demo User",
    playbackBackground: "Screen off",
    playbackPause: "Pause",
    playbackResume: "Play",
    smartRefresh: "Smart",
    loopSingle: "Single",
    loopSequence: "Order",
    loopShuffle: "Shuffle",
    loopSmart: "Smart",
  },
  zh: {
    tabFeed: "首页",
    tabColumns: "栏目",
    tabDiscover: "发现",
    tabAdd: "添加",
    tabInbox: "消息",
    tabMe: "我的",
    columnsTitle: "栏目",
    all: "全部",
    freshVideos: "让你耳目一新的视频",
    games: "游戏",
    music: "音乐",
    shorts: "短片",
    live: "直播",
    searchPlaceholder: "搜索视频",
    castSearching: "正在搜索投屏设备...",
    notificationsEnabled: "已开启通知",
    notificationsDisabled: "已关闭通知",
    noVideos: "没有找到视频",
    profileFollowing: "关注",
    profileFollowers: "粉丝",
    profileLikes: "喜欢",
    profileEdit: "编辑",
    profileEditProfile: "编辑资料",
    profileFollow: "关注",
    profileMessage: "消息",
    profileDisplayName: "显示名称",
    demoUserName: "演示用户",
    playbackBackground: "息屏播放",
    playbackPause: "暂停",
    playbackResume: "继续",
    smartRefresh: "智能刷",
    loopSingle: "单曲",
    loopSequence: "顺序",
    loopShuffle: "随机",
    loopSmart: "智能",
  },
};

export type TranslationKey = keyof typeof translations.en;

export function t(key: TranslationKey) {
  return translations[appLanguage][key];
}

export function pickLocalized<T>(values: { en: T; zh: T }) {
  return values[appLanguage];
}
